import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FaBars, FaMoon, FaSearch, FaShoppingCart, FaSun, FaTimes, FaUserCircle } from 'react-icons/fa'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useTheme } from '../contexts/ThemeContext'
import { api } from '../../services/api'
import { CART_UPDATED_EVENT } from '../../services/cartEvents'
import './Header.css'

const navigationItems = [
  { labelKey: 'navHome', to: '/', isActive: (location) => location.pathname === '/' },
  {
    labelKey: 'navShop',
    to: '/boutique',
    isActive: (location) => location.pathname === '/boutique' || location.pathname === '/products'
  },
  {
    labelKey: 'navCollections',
    to: '/collections',
    isActive: (location) => location.pathname.startsWith('/collections')
  },
  { labelKey: 'navAbout', to: '/a-propos', isActive: (location) => location.pathname === '/a-propos' },
  { labelKey: 'navContact', to: '/contact', isActive: (location) => location.pathname === '/contact' }
]

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [cartCount, setCartCount] = useState(0)
  const { user, logout } = useAuth()
  const { language, t, toggleLanguage } = useLanguage()
  const { isDarkMode, toggleTheme } = useTheme()
  const location = useLocation()
  const isAdmin = user?.role === 'admin' || user?.email === 'admin@lampes.ma'
  const activeItems = useMemo(() => {
    const items = isAdmin
      ? [
          ...navigationItems,
          { labelKey: 'navUsers', to: '/admin/users', isActive: (current) => current.pathname === '/admin/users' },
          { labelKey: 'navOrders', to: '/admin/orders', isActive: (current) => current.pathname === '/admin/orders' },
          { labelKey: 'navProducts', to: '/admin/products', isActive: (current) => current.pathname === '/admin/products' }
        ]
      : navigationItems

    return items.map((item) => ({ ...item, active: item.isActive(location) }))
  }, [isAdmin, location])

  const handleMenuToggle = () => {
    setIsMenuOpen((value) => !value)
  }

  const handleLinkClick = () => {
    setIsMenuOpen(false)
  }

  useEffect(() => {
    const fetchCount = async () => {
      if (!user) {
        setCartCount(0)
        return
      }

      try {
        const response = await api.get('/cart/count', { skipAuthRedirect: true })
        setCartCount(response.data?.data?.count || 0)
      } catch (error) {
        setCartCount(0)
      }
    }

    fetchCount()
    window.addEventListener(CART_UPDATED_EVENT, fetchCount)

    return () => {
      window.removeEventListener(CART_UPDATED_EVENT, fetchCount)
    }
  }, [user, location.pathname])

  return (
    <header className="header-shell">
      <div className="container">
        <div className="header">
          <Link to="/" className="logo" onClick={handleLinkClick}>
            <span>SOLAR</span><strong>LIGHT</strong>
          </Link>

          <nav className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            {activeItems.map((item) => (
              <Link
                key={item.labelKey}
                to={item.to}
                className={`nav-link ${item.active ? 'active' : ''}`}
                onClick={handleLinkClick}
              >
                {t(item.labelKey)}
              </Link>
            ))}
          </nav>

          <div className="header-tools">
            <button
              type="button"
              className="language-toggle"
              onClick={toggleLanguage}
              aria-label={t('languageLabel')}
              title={t('languageLabel')}
              data-no-translate
            >
              <span className={`language-option ${language === 'en' ? 'active' : ''}`}>EN</span>
              <span className={`language-option ${language === 'fr' ? 'active' : ''}`}>FR</span>
            </button>

            <button
              type="button"
              className={`theme-toggle ${isDarkMode ? 'is-dark' : 'is-light'}`}
              onClick={toggleTheme}
              aria-label={isDarkMode ? t('themeLightLabel') : t('themeDarkLabel')}
              title={isDarkMode ? t('themeLightLabel') : t('themeDarkLabel')}
              data-no-translate
            >
              <span className="theme-toggle-thumb">
                {isDarkMode ? <FaSun /> : <FaMoon />}
              </span>
            </button>

            <Link to="/boutique" className="header-search" aria-label={t('searchProducts')}>
              <FaSearch />
            </Link>

            <Link to="/cart" className="icon-btn" aria-label={t('openCart')}>
              <FaShoppingCart />
              <span className="cart-pill">{cartCount}</span>
            </Link>

            {user ? (
              <div className="user-menu">
                <button className="icon-btn user-btn" type="button" aria-label={t('openAccountMenu')}>
                  <FaUserCircle />
                </button>
                <div className="user-dropdown glass-card">
                  <div className="user-dropdown-profile">
                    <strong>{`${user.prenom || ''} ${user.nom || ''}`.trim() || t('customerFallback')}</strong>
                    <small>{user.email || t('connectedAccount')}</small>
                  </div>
                  <Link to="/dashboard" onClick={handleLinkClick}>{t('myAccount')}</Link>
                  <Link to="/orders" onClick={handleLinkClick}>{t('myOrders')}</Link>
                  <Link to="/cart" onClick={handleLinkClick}>{t('cart')}</Link>
                  <Link to={isAdmin ? '/dashboard?section=messages' : '/dashboard'} onClick={handleLinkClick}>{t('messages')}</Link>
                  <button type="button" onClick={logout}>{t('logout')}</button>
                </div>
              </div>
            ) : (
              <Link to="/login" className="icon-btn login-btn" aria-label={t('login')}>
                <FaUserCircle />
              </Link>
            )}

            <button
              type="button"
              className="mobile-menu-btn"
              onClick={handleMenuToggle}
              aria-label={t('showMenu')}
            >
              {isMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
